import React from 'react';

import { InputNumber, Switch, Checkbox } from 'antd';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { timeArrayToString, timeStringToArray } from '@libs/NwMoment';

import CustomDatePicker from '@components/EventDateTimePicker/CustomDatePicker';
import TimePicker from '@components/EventDateTimePicker/TimePicker';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .period-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;

        p {
            width: 3.6rem;
            font-size: 12px;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin: 0;
        }

        .date-pickers {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
        }
    }

    .recurrence-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;

        p {
            width: 3.6rem;
            font-size: 12px;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin: 0;
        }

        .every-input {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            .ant-input-number {
                width: 60px;
            }
        }
    }

    .days-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;

        p {
            width: 3.6rem;
            font-size: 12px;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin: 0;
        }

        .days-checkboxes {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }
    }

    .time-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 1rem;
        flex-wrap: wrap;

        p {
            width: 3.6rem;
            font-size: 12px;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            margin: 0;
        }

        .all-day-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
        }
    }
`;

const DAYS_OF_WEEK = [
    { label: 'Mon', value: 'Monday' },
    { label: 'Tue', value: 'Tuesday' },
    { label: 'Wed', value: 'Wednesday' },
    { label: 'Thu', value: 'Thursday' },
    { label: 'Fri', value: 'Friday' },
    { label: 'Sat', value: 'Saturday' },
    { label: 'Sun', value: 'Sunday' },
];

const RecurrenceSelector = ({ value, onChange }) => {
    if (!value) {
        return null;
    }

    const { startDate, endDate, every, daysOfWeek, startTime, endTime, allDay } = value;

    const handleUpdateStartDate = (date) => {
        let newEndDate = endDate;
        if (getMoment(date).isAfter(endDate)) {
            newEndDate = getMoment(date).add(6, 'months');
        }
        onChange({
            ...value,
            startDate: date,
            endDate: newEndDate,
        });
    };

    const handleUpdateEndDate = (date) => {
        onChange({
            ...value,
            endDate: date,
        });
    };

    const handleEveryChange = (val) => {
        onChange({
            ...value,
            every: val || 1,
        });
    };

    const handleDaysChange = (day) => {
        const newDays = daysOfWeek.includes(day) ? daysOfWeek.filter((d) => d !== day) : [...daysOfWeek, day];
        onChange({
            ...value,
            daysOfWeek: newDays,
        });
    };

    const handleSetAllDay = (checked) => {
        if (checked) {
            onChange({
                ...value,
                allDay: checked,
                startTime: [0, 0],
                endTime: [23, 59],
            });
        } else {
            onChange({
                ...value,
                allDay: checked,
                startTime: [9, 0],
                endTime: [9, 0],
            });
        }
    };

    const handleChangeStartTime = (time) => {
        onChange({
            ...value,
            startTime: timeStringToArray(time),
        });
    };

    const handleChangeEndTime = (time) => {
        onChange({
            ...value,
            endTime: timeStringToArray(time),
        });
    };

    const disabledEndDate = (date) => {
        const days = date.diff(startDate, 'days');
        if (days >= 0 && days <= 365) {
            return false;
        }
        return true;
    };

    return (
        <Wrapper>
            <div className="period-row">
                <p>Period</p>
                <div className="date-pickers">
                    <CustomDatePicker
                        label="Start Date"
                        name="startDate"
                        value={startDate}
                        onChange={handleUpdateStartDate}
                    />
                    <span>to</span>
                    <CustomDatePicker
                        label="End Date"
                        name="endDate"
                        value={endDate}
                        disabledRange={disabledEndDate}
                        onChange={handleUpdateEndDate}
                    />
                </div>
            </div>

            <div className="recurrence-row">
                <p>Every</p>
                <div className="every-input">
                    <InputNumber min={1} max={52} value={every} onChange={handleEveryChange} />
                    <span>week(s)</span>
                </div>
            </div>

            <div className="days-row">
                <p>Days</p>
                <div className="days-checkboxes">
                    {DAYS_OF_WEEK.map((day) => (
                        <Checkbox
                            key={day.value}
                            checked={daysOfWeek.includes(day.value)}
                            onChange={() => handleDaysChange(day.value)}
                        >
                            {day.label}
                        </Checkbox>
                    ))}
                </div>
            </div>

            <div className="time-row">
                <p>Time</p>
                <div className="all-day-container">
                    <Switch checked={allDay} onChange={handleSetAllDay} />
                    <span>all day</span>
                </div>
                {!allDay && (
                    <>
                        <TimePicker value={timeArrayToString(startTime)} onChange={handleChangeStartTime} />
                        <span>to</span>
                        <TimePicker value={timeArrayToString(endTime)} onChange={handleChangeEndTime} />
                    </>
                )}
            </div>
        </Wrapper>
    );
};

export default RecurrenceSelector;
